import React, { useEffect, useMemo, useRef, useState } from "react";
import { APP_VERSION } from "../lib/app-info";

/**
 * Pink Pixel Terminal — Integrated Terminal for Pink Pixel OS
 * ------------------------------------------------------
 * • Colorful-on-dark default theme matching Pink Pixel OS aesthetic
 * • Command history (↑/↓), Ctrl+L to clear, Tab to autocomplete
 * • Basic commands: help, clear, about, theme [list|set <name>], echo, date, time, whoami
 * • projects [--refresh]: list GitHub repos for a username (default: "pinkpixel-dev")
 * • set github <username>: set the user for projects command
 * • open <index|repoName>: open a repo printed by last `projects` call
 * • os: show Pink Pixel OS system information
 * • apps: list all available Pink Pixel OS applications
 * 
 * Integrated with Pink Pixel OS window management system
 */

// --- Themes matching Pink Pixel OS aesthetic ---
const THEMES = {
  pixel: {
    name: "Pink Pixel (Default)",
    bg: "rgba(11, 15, 26, 0.95)",
    panel: "rgba(244, 114, 182, 0.1)",
    text: "#e5e7eb",
    dim: "#97a3b3",
    prompt: "#f472b6",
    accent: "#7c3aed",
    success: "#34d399",
    warn: "#f59e0b",
    error: "#fb7185",
    glow: "0 0 24px rgba(244,114,182,0.35)",
  },
  matrix: {
    name: "Matrix",
    bg: "rgba(2, 11, 2, 0.95)",
    panel: "rgba(0,255,0,0.06)",
    text: "#c1f9c1",
    dim: "#7ddc7d",
    prompt: "#22c55e",
    accent: "#16a34a",
    success: "#86efac",
    warn: "#a3e635",
    error: "#f87171",
    glow: "0 0 24px rgba(34,197,94,0.35)",
  },
  neon: {
    name: "Neon",
    bg: "rgba(10, 10, 18, 0.95)",
    panel: "rgba(255,0,255,0.06)",
    text: "#f5f5ff",
    dim: "#cfcff8",
    prompt: "#ff00ff",
    accent: "#00eaff",
    success: "#39ff14",
    warn: "#fffb00",
    error: "#ff4d6d",
    glow: "0 0 24px rgba(255,0,255,0.3)",
  },
  ocean: {
    name: "Ocean",
    bg: "rgba(8, 19, 28, 0.95)",
    panel: "rgba(56,189,248,0.06)",
    text: "#e6f6ff",
    dim: "#a6d6f0",
    prompt: "#38bdf8",
    accent: "#0ea5e9",
    success: "#22d3ee",
    warn: "#fde047",
    error: "#fb7185",
    glow: "0 0 24px rgba(14,165,233,0.35)",
  },
  sunset: {
    name: "Sunset",
    bg: "rgba(20, 11, 14, 0.95)",
    panel: "rgba(251,113,133,0.06)",
    text: "#fff0f2",
    dim: "#f7c7cf",
    prompt: "#fb7185",
    accent: "#f59e0b",
    success: "#fbbf24",
    warn: "#fcd34d",
    error: "#ef4444",
    glow: "0 0 24px rgba(251,113,133,0.35)",
  },
};

// helpers
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

function Row({ children }) {
  return <div className="leading-relaxed">{children}</div>;
}
function K({ children }) {
  return <code className="px-1.5 py-0.5 rounded bg-white/10">{children}</code>;
}

function PinkPixelBanner({ theme }) {
  return (
    <div className="font-mono text-xs leading-tight mb-4">
      <Row>
        <span style={{ color: theme.prompt }}>██████╗ ██╗███╗   ██╗██╗  ██╗    </span>
        <span style={{ color: theme.accent }}>██████╗ ██╗██╗  ██╗███████╗██╗     </span>
      </Row>
      <Row>
        <span style={{ color: theme.prompt }}>██╔══██╗██║████╗  ██║██║ ██╔╝    </span>
        <span style={{ color: theme.accent }}>██╔══██╗██║╚██╗██╔╝██╔════╝██║     </span>
      </Row>
      <Row>
        <span style={{ color: theme.success }}>██████╔╝██║██╔██╗ ██║█████╔╝     </span>
        <span style={{ color: theme.warn }}>██████╔╝██║ ╚███╔╝ █████╗  ██║     </span>
      </Row>
      <Row>
        <span style={{ color: theme.success }}>██╔═══╝ ██║██║╚██╗██║██╔═██╗     </span>
        <span style={{ color: theme.warn }}>██╔═══╝ ██║ ██╔██╗ ██╔══╝  ██║     </span>
      </Row>
      <Row>
        <span style={{ color: theme.error }}>██║     ██║██║ ╚████║██║  ██╗    </span>
        <span style={{ color: theme.dim }}>██║     ██║██╔╝ ██╗███████╗███████╗</span>
      </Row>
      <Row>
        <span style={{ color: theme.error }}>╚═╝     ╚═╝╚═╝  ╚═══╝╚═╝  ╚═╝    </span>
        <span style={{ color: theme.dim }}>╚═╝     ╚═╝╚═╝  ╚═╝╚══════╝╚══════╝</span>
      </Row>
      <Row>
        <span style={{ color: theme.text }}>                                   </span>
        <span style={{ color: theme.dim }}>    "Dream it, Pixel it" ✨</span>
      </Row>
    </div>
  );
}

function BuiltinHelp() {
  return (
    <div className="space-y-1">
      <Row>
        <K>help</K> — list all commands
      </Row>
      <Row>
        <K>about</K> — about Pink Pixel OS
      </Row>
      <Row>
        <K>os</K> — system information
      </Row>
      <Row>
        <K>apps</K> — list available applications
      </Row>
      <Row>
        <K>banner</K> — show Pink Pixel banner
      </Row>
      <Row>
        <K>whoami</K>, <K>date</K>, <K>time</K>, <K>echo &lt;msg&gt;</K>
      </Row>
      <Row>
        <K>clear</K> — clear screen (or <K>Ctrl+L</K>)
      </Row>
      <Row>
        <K>theme list</K> / <K>theme set &lt;name&gt;</K>
      </Row>
      <Row>
        <K>projects</K> [<K>--refresh</K>] — list GitHub repos
      </Row>
      <Row>
        <K>set github &lt;username&gt;</K> — change GitHub user
      </Row>
      <Row>
        <K>open &lt;index|name&gt;</K> — open repo from last projects list
      </Row>
      <Row>
        <span style={{ color: '#666' }}>Try typing '<K>konami</K>' for a surprise... 🎮</span>
      </Row>
    </div>
  );
}

// --- Main Component --------------------------------------------------------
const TerminalApp = () => {
  // read persisted values lazily (safe for SSR)
  const [themeId, setThemeId] = useState(() => {
    try { return localStorage.getItem("pp_terminal_theme") || "pixel"; } catch { return "pixel"; }
  });
  const [githubUser, setGithubUser] = useState(() => {
    try { return localStorage.getItem("pp_terminal_github_user") || "pinkpixel-dev"; } catch { return "pinkpixel-dev"; }
  });

  const theme = THEMES[themeId] || THEMES.pixel;

  // terminal state
  const [lines, setLines] = useState([
    <PinkPixelBanner theme={theme} key="banner" />,
    colorLine(theme.accent, '🌸 Welcome to Pink Pixel Terminal • type "help" to begin'),
    colorLine(theme.dim, `✨ Integrated with Pink Pixel OS v${APP_VERSION}`),
  ]);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [histIndex, setHistIndex] = useState(-1);
  const [repos, setRepos] = useState(null);
  const [lastProjectPrint, setLastProjectPrint] = useState([]);

  const inputRef = useRef(null);
  const scrollerRef = useRef(null);

  // available commands for autocomplete
  const commands = useMemo(
    () => [
      "help",
      "about",
      "banner",
      "os",
      "apps", 
      "whoami",
      "echo",
      "clear",
      "date",
      "time",
      "theme",
      "projects",
      "set",
      "open",
      "konami",
      "matrix",
      "cowsay",
    ],
    []
  );

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { scrollerRef.current?.scrollTo(0, scrollerRef.current?.scrollHeight || 0); }, [lines]);

  useEffect(() => { try { localStorage.setItem('pp_terminal_theme', themeId); } catch {} }, [themeId]);
  useEffect(() => { try { localStorage.setItem('pp_terminal_github_user', githubUser); } catch {} }, [githubUser]);

  // expose CSS vars for quick theming
  const vars = {
    "--term-bg": theme.bg,
    "--term-panel": theme.panel,
    "--term-text": theme.text,
    "--term-dim": theme.dim,
    "--term-prompt": theme.prompt,
    "--term-accent": theme.accent,
    "--term-success": theme.success,
    "--term-warn": theme.warn,
    "--term-error": theme.error,
    "--term-glow": theme.glow,
  };

  const prompt = useMemo(() => {
    return [
      spanColor(theme.prompt, "pp"),
      spanColor(theme.text, "@"),
      spanColor(theme.accent, "os"),
      spanColor(theme.text, ":"),
      spanColor(theme.dim, "~"),
      spanColor(theme.text, "$"),
    ].map((n, i) => (
      <span key={i} className="mr-1">
        {n}
      </span>
    ));
  }, [theme]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const raw = input.trim();
    const renderedPrompt = renderPrompt(raw, theme);
    setLines((prev) => [...prev, renderedPrompt]);
    if (!raw) return setInput("");

    // history mgmt
    setHistory((h) => [raw, ...h]);
    setHistIndex(-1);

    const parts = raw.split(/\s+/);
    const cmd = (parts[0] || "").toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      case "help":
        add(<BuiltinHelp />);
        break;
      case "clear":
        setLines([]);
        break;
      case "about":
        add(aboutBlock(theme));
        break;
      case "os":
        add(osInfoBlock(theme));
        break;
      case "apps":
        add(appsListBlock(theme));
        break;
      case "banner":
        add(<PinkPixelBanner theme={theme} />);
        break;
      case "konami":
        add(konamiEasterEgg(theme));
        break;
      case "matrix":
        add(matrixEffect(theme));
        break;
      case "cowsay":
        const message = args.join(" ") || "Hello from Pink Pixel OS!";
        add(cowsayArt(message, theme));
        break;
      case "whoami":
        add(ok("Pink Pixel — Creator of Pink Pixel OS & AI Developer"));
        break;
      case "echo":
        add(args.length ? text(args.join(" ")) : dim(""));
        break;
      case "date":
        add(text(new Date().toLocaleDateString()));
        break;
      case "time":
        add(text(new Date().toLocaleTimeString()));
        break;
      case "theme": {
        const sub = (args[0] || "").toLowerCase();
        if (!sub || sub === "list") {
          add(text("Available terminal themes:"));
          Object.entries(THEMES).forEach(([k, v]) => add(dim(` - ${k}  ${v.name}${k === themeId ? "  (current)" : ""}`)));
        } else if (sub === "set") {
          const name = (args[1] || "").toLowerCase();
          if (!THEMES[name]) add(err(`Unknown theme "${name}". Try: theme list`));
          else {
            setThemeId(name);
            add(ok(`Terminal theme set to ${name}`));
          }
        } else {
          add(err("Usage: theme [list|set <name>]"));
        }
        break;
      }
      case "projects": {
        const doRefresh = args.includes("--refresh");
        await handleProjects(doRefresh);
        break;
      }
      case "set": {
        const key = (args[0] || "").toLowerCase();
        if (key === "github" || key === "github-user" || key === "gh") {
          const u = args[1];
          if (!u) add(err("Usage: set github <username>"));
          else {
            setGithubUser(u);
            setRepos(null);
            add(ok(`GitHub user set to ${u}. Run "projects" to load repos.`));
          }
        } else {
          add(err("Unknown setting. Try: set github <username>"));
        }
        break;
      }
      case "open": {
        if (!lastProjectPrint?.length) { add(err("No projects printed yet. Run \"projects\" first.")); break; }
        const target = args.join(" ");
        if (!target) { add(err("Usage: open <index|repoName>")); break; }
        let repo = null;
        const idx = Number.isFinite(+target) ? +target : NaN;
        if (!Number.isNaN(idx)) {
          const real = clamp(idx - 1, 0, lastProjectPrint.length - 1);
          repo = lastProjectPrint[real];
        } else {
          repo = lastProjectPrint.find((r) => r.name.toLowerCase() === target.toLowerCase());
        }
        if (!repo) add(err(`Cannot find repo "${target}"`));
        else window.open(repo.html_url, "_blank", "noopener,noreferrer");
        break;
      }
      default:
        add(err(`Command not found: ${cmd}. Try "help".`));
        break;
    }

    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const token = input.trim();
      if (!token) return;
      const match = commands.find((c) => c.startsWith(token.toLowerCase()));
      if (match) setInput(match + " ");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHistIndex((i) => {
        const next = clamp(i + 1, -1, history.length - 1);
        if (next >= 0) setInput(history[next]);
        return next;
      });
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHistIndex((i) => {
        const next = clamp(i - 1, -1, history.length - 1);
        if (next >= 0) setInput(history[next]);
        else setInput("");
        return next;
      });
    } else if (e.ctrlKey && (e.key === "l" || e.key === "L")) {
      e.preventDefault();
      setLines([]);
    }
  };

  // output helpers
  function add(node) { setLines((prev) => [...prev, node]); }
  function text(t) { return <Row>{t}</Row>; }
  function dim(t) { return <Row><span style={{ color: theme.dim }}>{t}</span></Row>; }
  function ok(t) { return <Row><span style={{ color: theme.success }}>{t}</span></Row>; }
  function err(t) { return <Row><span style={{ color: theme.error }}>{t}</span></Row>; }

  async function handleProjects(forceRefresh) {
    try {
      if (!forceRefresh && Array.isArray(repos) && repos.length) {
        printRepos(repos, `Cached projects for ${githubUser}`);
        return;
      }
      add(dim(`Fetching projects for ${githubUser}…`));
      const res = await fetch(
        `https://api.github.com/users/${encodeURIComponent(githubUser)}/repos?sort=updated&per_page=25`
      );
      if (!res.ok) throw new Error(`GitHub API error ${res.status}`);
      const data = await res.json();
      const sorted = [...data].sort((a, b) => new Date(b.pushed_at) - new Date(a.pushed_at));
      setRepos(sorted);
      printRepos(sorted, `Projects for ${githubUser}`);
    } catch (e) {
      add(err(`Failed to fetch repos (${e.message}). Showing demo list.`));
      const demo = [
        { name: 'pink-pixel-os', description: 'Futuristic React desktop OS simulator', stargazers_count: 420, language: 'JavaScript', html_url: 'https://github.com/pinkpixel-dev/pink-pixel-os' },
        { name: 'crumb-ai-chatbot', description: 'Sweet cupcake AI assistant', stargazers_count: 133, language: 'TypeScript', html_url: 'https://github.com/pinkpixel-dev/crumb-ai-chatbot' },
        { name: 'glass-color-picker', description: 'Professional color picker tool', stargazers_count: 89, language: 'React', html_url: 'https://github.com/pinkpixel-dev/glass-color-picker' },
      ];
      setRepos(demo);
      printRepos(demo, 'Demo projects');
    }
  }

  function printRepos(list, title) {
    add(text(title));
    const rows = list.slice(0, 10).map((r, i) => ({
      idx: i + 1,
      name: r.name,
      desc: r.description || '',
      lang: r.language || '—',
      stars: r.stargazers_count || 0,
      html_url: r.html_url,
    }));
    setLastProjectPrint(rows);
    rows.forEach((r) =>
      add(
        <Row key={r.idx}>
          <span style={{ color: theme.accent }}>[{r.idx}]</span>{' '}
          <span className="font-semibold">{r.name}</span>{' '}
          <span style={{ color: theme.dim }}>• {r.lang} • ★ {r.stars}</span>
          {r.desc ? <span style={{ color: theme.dim }}> — {r.desc}</span> : null}
        </Row>
      )
    );
    add(dim('Use "open <index|name>" to view on GitHub.'));
  }

  return (
    <div 
      className="h-full w-full flex flex-col font-mono text-sm"
      style={vars}
    >
      {/* Terminal Header */}
      <div
        className="flex-shrink-0 px-4 py-3 border-b border-white/10"
        style={{ 
          background: 'var(--term-panel)', 
          borderColor: 'rgba(255,255,255,0.12)', 
          boxShadow: 'var(--term-glow)' 
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: theme.error }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: theme.warn }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: theme.success }} />
            <div className="ml-3 font-semibold" style={{ color: theme.text }}>
              🌸 Pink Pixel Terminal
            </div>
            <div className="text-xs" style={{ color: theme.dim }}>
              theme: {themeId} • github: {githubUser}
            </div>
          </div>
          <div className="text-xs" style={{ color: theme.dim }}>
            type <span className="font-mono">help</span>
          </div>
        </div>
      </div>

      {/* Terminal Body */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-auto p-4"
        style={{ 
          background: 'rgba(0,0,0,0.25)', 
          color: 'var(--term-text)' 
        }}
        aria-live="polite"
      >
        <div className="space-y-1">
          {lines.map((row, i) => (
            <div key={i}>{row}</div>
          ))}
        </div>

        <form onSubmit={onSubmit} className="mt-2 flex items-center gap-2">
          <div className="shrink-0 flex items-center">{prompt}</div>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-transparent flex-1 outline-none placeholder-white/30"
            aria-label="Terminal input"
            placeholder="type a command…"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
          />
        </form>
      </div>
    </div>
  );
};

// --- Render helpers --------------------------------------------------------
function renderPrompt(cmd, theme) {
  return (
    <Row>
      <span style={{ color: theme.prompt }} className="mr-1">pp</span>
      <span className="mr-1">@</span>
      <span style={{ color: theme.accent }} className="mr-1">os</span>
      <span className="mr-1">:</span>
      <span style={{ color: theme.dim }} className="mr-1">~</span>
      <span className="mr-2">$</span>
      <span>{cmd}</span>
    </Row>
  );
}
function spanColor(color, text) { return <span style={{ color }}>{text}</span>; }
function colorLine(color, text) { return (<Row><span style={{ color }}>{text}</span></Row>); }

function aboutBlock(theme) {
  return (
    <div className="space-y-1">
      <Row>
        <span className="font-semibold" style={{ color: theme.accent }}>Pink Pixel OS</span>{' '}— futuristic desktop OS simulator in your browser.
      </Row>
      <Row>Built by Pink Pixel with React • Tailwind • AI-powered features ✨</Row>
      <Row>
        Try <K>projects</K> to see recent GitHub work, <K>apps</K> to list OS applications, or <K>theme list</K> to customize.
      </Row>
    </div>
  );
}

function osInfoBlock(theme) {
  return (
    <div className="space-y-1">
      <Row>
        <span style={{ color: theme.accent }}>Pink Pixel OS v{APP_VERSION}</span> — React Desktop Simulator
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>Architecture:</span> React 18 + Vite + Tailwind CSS
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>Features:</span> 18 Apps • Multi-Window • Real APIs • Theming
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>Components:</span> 49 React components • Advanced window management
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>Integrations:</span> OpenWeatherMap • OpenAI • Spotify • GitHub
      </Row>
      <Row>
        <span style={{ color: theme.success }}>Status:</span> Production Ready ✨
      </Row>
    </div>
  );
}

function appsListBlock(theme) {
  const apps = [
    "🗂️ Portfolio File Explorer", "🎵 Pink Pixel Music Player", "🧁 Crumb AI Chatbot",
    "🎨 Glass Color Picker", "🎮 Game Center (Tetris, Snake)", "🌤️ Weather Center",
    "🌐 Web Browser", "📷 Photo Viewer", "🧮 Calculator", "🎵 Spotify Player",
    "📝 Notepad", "⚙️ Settings", "📱 Applications Launcher", "📺 YouTube Player",
    "📧 Mail Client", "📅 Calendar", "🛍️ App Store", "📰 News Reader",
    "🖥️ Terminal (this app!)"
  ];
  
  return (
    <div className="space-y-1">
      <Row>
        <span style={{ color: theme.accent }}>Pink Pixel OS Applications</span> ({apps.length} total)
      </Row>
      {apps.map((app, i) => (
        <Row key={i}>
          <span style={{ color: theme.dim }}>  {app}</span>
        </Row>
      ))}
      <Row>
        <span style={{ color: theme.dim }}>All apps available through desktop icons, Start Menu, or Apps launcher!</span>
      </Row>
    </div>
  );
}

// --- Easter Egg Functions ---
function konamiEasterEgg(theme) {
  return (
    <div className="space-y-1">
      <Row>
        <span style={{ color: theme.success }}>🎮 KONAMI CODE ACTIVATED! 🎮</span>
      </Row>
      <Row>
        <span style={{ color: theme.prompt }}>⬆️⬆️⬇️⬇️⬅️➡️⬅️➡️🅱️🅰️</span>
      </Row>
      <Row>
        <span style={{ color: theme.accent }}>🎊 Congratulations! You found the secret! 🎊</span>
      </Row>
      <Row>
        <span style={{ color: theme.warn }}>✨ Pink Pixel Power Activated! ✨</span>
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>Fun fact: This classic cheat code originated from Gradius (1985)</span>
      </Row>
      <Row>
        <span style={{ color: theme.success }}>🎯 Achievement Unlocked: "Retro Gamer" 🎯</span>
      </Row>
    </div>
  );
}

function matrixEffect(theme) {
  const matrixChars = [
    "01001000 01100101 01101100 01101100 01101111",
    "01010000 01101001 01101110 01101011 00100000",
    "01010000 01101001 01111000 01100101 01101100",
    "ﾊﾟｽ ｺﾝﾋﾟｭｰﾀｰ ﾃﾞｰﾀ",
    "Wake up, Pink Pixel...",
    "The Matrix has you...",
    "Follow the pink rabbit 🐰"
  ];
  
  return (
    <div className="space-y-1">
      <Row>
        <span style={{ color: theme.success }}>🕶️ ENTERING THE MATRIX... 🕶️</span>
      </Row>
      {matrixChars.map((line, i) => (
        <Row key={i}>
          <span style={{ color: i < 3 ? theme.success : theme.accent }}>{line}</span>
        </Row>
      ))}
      <Row>
        <span style={{ color: theme.warn }}>💊 Take the pink pill and stay in Pink Pixel OS 💊</span>
      </Row>
    </div>
  );
}

function cowsayArt(message, theme) {
  const msgLength = message.length;
  const topBorder = " " + "_".repeat(msgLength + 2);
  const bottomBorder = " " + "-".repeat(msgLength + 2);
  
  return (
    <div className="space-y-0 font-mono text-xs">
      <Row>
        <span style={{ color: theme.dim }}>{topBorder}</span>
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>( </span>
        <span style={{ color: theme.text }}>{message}</span>
        <span style={{ color: theme.dim }}> )</span>
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>{bottomBorder}</span>
      </Row>
      <Row>
        <span style={{ color: theme.prompt }}>        \   ^__^</span>
      </Row>
      <Row>
        <span style={{ color: theme.prompt }}>         \  (oo)\_______</span>
      </Row>
      <Row>
        <span style={{ color: theme.prompt }}>            (__)\       )\/\</span>
      </Row>
      <Row>
        <span style={{ color: theme.accent }}>             🌸 ||----w |</span>
      </Row>
      <Row>
        <span style={{ color: theme.accent }}>                ||     ||</span>
      </Row>
      <Row>
        <span style={{ color: theme.dim }}>~ Pink Pixel Cow says hello! 🐄✨</span>
      </Row>
    </div>
  );
}

export default TerminalApp;
